import type { CSSProperties } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// AMS COLOR PALETTE  —  Primary #8F295E  |  Font: Roboto
// ─────────────────────────────────────────────────────────────────────────────

export const colors = {
  // ── Brand primary (maroon/burgundy) ──────────────────────────────────────
  primary:     "#8F295E",
  primaryDark: "#7a2350",
  primaryLight:"#a83672",
  primaryGlow: "rgba(143,41,94,0.25)",

  // ── Dark-mode backgrounds (deep maroon tones) ────────────────────────────
  navy:        "#1a0810",  // page background
  navyMid:     "#220c18",  // slightly lighter bg
  navyLight:   "#2d1020",  // card / panel background
  navyDeep:    "#0d0408",  // deepest shadow bg

  // ── Light-mode backgrounds ───────────────────────────────────────────────
  lightBg:     "#FFFFFF",
  lightSurface:"#F5F5F5",
  lightBorder: "#E5E7EB",

  // ── Text hierarchy ────────────────────────────────────────────────────────
  text:        "#FFE6E6",   // primary — warm white (dark-mode)
  textLight:   "#4E4E4E",   // primary — dark (light-mode)
  slateLight:  "#e8c4d4",   // secondary on dark
  slate:       "#c4899e",   // tertiary / captions
  muted:       "#8a5070",   // disabled / placeholders
  dim:         "#4a2040",   // subtle borders

  // ── Semantic aliases (kept for backward-compat with existing components) ──
  /** accent colour — was green, now primary maroon */
  green:       "#8F295E",
  greenDark:   "#7a2350",
  /** link / info colour — was blue, now primary maroon */
  blue:        "#8F295E",
  blueMid:     "#7a2350",
} as const;

export type ThemeColor = keyof typeof colors;

// ─────────────────────────────────────────────────────────────────────────────
// FONT FAMILY
// ─────────────────────────────────────────────────────────────────────────────
export const fontFamily = "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ─────────────────────────────────────────────────────────────────────────────
// TYPOGRAPHY SCALE  (matches Roboto spec: Regular 400 / Medium 500 / SemiBold 600)
// ─────────────────────────────────────────────────────────────────────────────
export const typography = {
  h1: { fontSize: "32px", lineHeight: "40px" },
  h2: { fontSize: "28px", lineHeight: "36px" },
  h3: { fontSize: "24px", lineHeight: "32px" },
  h4: { fontSize: "20px", lineHeight: "28px" },
  h5: { fontSize: "14px", lineHeight: "20px" },
  body: { fontSize: "16px", lineHeight: "24px" },
  sm:   { fontSize: "14px", lineHeight: "20px" },
  xs:   { fontSize: "12px", lineHeight: "16px" },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// FONT WEIGHTS
// ─────────────────────────────────────────────────────────────────────────────
export const fw = {
  regular:   400,
  medium:    500,
  semibold:  600,
  bold:      700,
  extrabold: 800,
  black:     900,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// GRADIENTS
// ─────────────────────────────────────────────────────────────────────────────
export const gradients = {
  /** Full-page hero background */
  hero: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.primary} 55%, #5a1540 80%, ${colors.navy} 100%)`,
  /** Navbar background */
  navbar: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.primaryDark} 100%)`,
  /** Primary button fill */
  primaryBtn: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
  /** Hover state for dark feature cards */
  cardHover: `linear-gradient(145deg, #2d1020, #4a1535)`,
  /** Login left panel */
  loginPanel: `linear-gradient(160deg, #8F295E 0%, #5a1540 50%, #3a0c28 100%)`,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE INLINE STYLE OBJECTS
// ─────────────────────────────────────────────────────────────────────────────

/** Standard max-width content container */
export const containerStyle: CSSProperties = {
  maxWidth: "1200px",
  margin:   "0 auto",
  padding:  "0 24px",
};

/** Small uppercase section eyebrow label */
export const sectionLabelStyle: CSSProperties = {
  color:          colors.primary,
  fontSize:       "11px",
  letterSpacing:  "3px",
  fontWeight:     fw.bold,
  textTransform:  "uppercase",
  marginBottom:   "14px",
  fontFamily,
};

/** Large section heading */
export const sectionHeadingStyle: CSSProperties = {
  color:          colors.text,
  fontSize:       "clamp(22px, 2.8vw, 34px)",
  fontWeight:     fw.extrabold,
  margin:         "0 0 16px 0",
  lineHeight:     1.2,
  letterSpacing:  "-0.3px",
  fontFamily,
};

/** Dark card panel (feature / role cards) */
export const cardDarkStyle: CSSProperties = {
  background:   colors.navyLight,
  border:       "1px solid rgba(143,41,94,0.15)",
  borderRadius: "12px",
  padding:      "24px",
};

/** Primary CTA button */
export const primaryBtnStyle: CSSProperties = {
  background:     colors.primary,
  color:          "#ffffff",
  border:         "none",
  borderRadius:   "9px",
  padding:        "12px 28px",
  fontWeight:     fw.bold,
  fontSize:       "14px",
  letterSpacing:  "0.4px",
  cursor:         "pointer",
  display:        "inline-block",
  textDecoration: "none",
  transition:     "all 0.2s ease",
  fontFamily,
};

/** Ghost / outline CTA button */
export const ghostBtnStyle: CSSProperties = {
  background:     "transparent",
  color:          "#e2e8f0",
  border:         "1px solid rgba(255,255,255,0.18)",
  borderRadius:   "9px",
  padding:        "12px 28px",
  fontWeight:     fw.semibold,
  fontSize:       "14px",
  letterSpacing:  "0.4px",
  cursor:         "pointer",
  display:        "inline-block",
  textDecoration: "none",
  transition:     "all 0.2s ease",
  fontFamily,
};

// Backward-compat aliases
export const greenBtnStyle = primaryBtnStyle;
